<template>
  <div class="seletor-produtos-garcom">
    <!-- Header fixo: busca sempre visivel + chips de categoria sticky -->
    <div class="spg-toolbar">
      <div class="spg-busca">
        <div class="spg-busca-input">
          <i class="fas fa-search spg-busca-icone" aria-hidden="true"></i>
          <input
            ref="buscaInput"
            v-model.trim="busca"
            type="search"
            class="form-control form-control-sm"
            placeholder="Nome, código ou código de barras"
            autocomplete="off"
            aria-label="Buscar produto"
          />
          <button
            v-if="busca"
            type="button"
            class="spg-busca-limpar"
            title="Limpar busca"
            @click="limparBusca"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="spg-chips">
        <button
          type="button"
          class="spg-chip"
          :class="{ 'spg-chip-ativo': categoriaAtiva === null }"
          :aria-pressed="String(categoriaAtiva === null)"
          @click="selecionarCategoria(null)"
        >
          Todos
        </button>
        <button
          v-for="grupo in categorias"
          :key="grupo.id"
          type="button"
          class="spg-chip"
          :class="{ 'spg-chip-ativo': categoriaAtiva === grupo.id }"
          :aria-pressed="String(categoriaAtiva === grupo.id)"
          @click="selecionarCategoria(grupo.id)"
        >
          {{ grupo.nome }}
        </button>
      </div>
    </div>

    <!-- Corpo rolavel (altura controlada pelo container do modal) -->
    <div
      class="spg-corpo"
      :aria-busy="String(carregando || carregandoMais)"
      @scroll.passive="onCatalogScroll"
    >
      <div
        v-if="carregando && !produtosAchatados.length"
        class="spg-estado spg-estado-load"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-spinner fa-spin"></i>
        <span>Carregando produtos...</span>
      </div>

      <div
        v-else-if="erro && !produtosAchatados.length"
        class="spg-estado spg-estado-erro"
        role="alert"
      >
        <span>{{ erro }}</span>
        <button
          type="button"
          class="btn btn-sm btn-light ml-2"
          @click="recarregar"
        >
          Tentar novamente
        </button>
      </div>

      <div v-else-if="!produtosAchatados.length" class="spg-estado">
        <template v-if="busca">
          Nenhum item encontrado para "{{ busca }}".
        </template>
        <template v-else-if="categoriaAtiva !== null">
          Nenhum produto encontrado nesta categoria.
        </template>
        <template v-else>Nenhum produto de restaurante encontrado.</template>
        <small class="d-block mt-1 text-muted">
          Verifique se os produtos estão marcados como restaurante e possuem uma
          categoria (grupo) no cadastro.
        </small>
        <button
          v-if="busca"
          type="button"
          class="btn btn-sm btn-link"
          @click="limparBusca"
        >
          Limpar busca
        </button>
      </div>

      <!-- A busca e a categoria já chegam filtradas pelo servidor. -->
      <div v-else-if="busca" class="spg-grid">
        <article
          v-for="produto in resultadosBusca"
          :key="'busca-' + produto.id"
          class="spg-card"
          :class="{ 'spg-card-adicionado': quantidadeAdicionada(produto.id) }"
        >
          <div class="spg-card-topo">
            <div class="spg-card-imagem" :style="imagemStyle(produto)">
              <span v-if="!produto.imagem" class="spg-card-iniciais">{{
                iniciais(produto.descricao)
              }}</span>
            </div>
            <b-badge
              v-if="quantidadeAdicionada(produto.id)"
              pill
              variant="primary"
              class="spg-card-badge"
            >
              {{ quantidadeAdicionada(produto.id) }}
            </b-badge>
          </div>
          <div class="spg-card-corpo">
            <div class="spg-card-nome">{{ produto.descricao }}</div>
            <div class="spg-card-preco">
              {{ formatMoney(produto.preco) }}
            </div>
            <div v-if="requiresConfiguration(produto)" class="spg-card-info">
              <i class="fas fa-sliders-h" aria-hidden="true"></i>
              Possui opções
            </div>
          </div>
          <div class="spg-card-acoes">
            <button
              v-if="!requiresConfiguration(produto)"
              type="button"
              class="btn btn-sm btn-primary spg-card-acao"
              :aria-label="`Adicionar ${produto.descricao}`"
              @click="adicionarRapido(produto)"
            >
              <i class="fas fa-plus" aria-hidden="true"></i>
              Adicionar
            </button>
            <button
              v-else
              type="button"
              class="btn btn-sm btn-primary spg-card-acao"
              :aria-label="`Configurar ${produto.descricao}`"
              @click="configurarProduto(produto)"
            >
              <i class="fas fa-sliders-h" aria-hidden="true"></i>
              Configurar
            </button>
          </div>
        </article>
      </div>

      <!-- Por categoria -->
      <div v-else>
        <div
          v-for="grupo in gruposVisiveis"
          :key="grupo.id"
          class="spg-categoria"
          :data-categoria-id="grupo.id"
        >
          <div class="spg-categoria-titulo">{{ grupo.nome }}</div>
          <div class="spg-grid">
            <article
              v-for="produto in grupo.produtos"
              :key="produto.id"
              class="spg-card"
              :class="{
                'spg-card-adicionado': quantidadeAdicionada(produto.id),
              }"
            >
              <div class="spg-card-topo">
                <div class="spg-card-imagem" :style="imagemStyle(produto)">
                  <span v-if="!produto.imagem" class="spg-card-iniciais">{{
                    iniciais(produto.descricao)
                  }}</span>
                </div>
                <b-badge
                  v-if="quantidadeAdicionada(produto.id)"
                  pill
                  variant="primary"
                  class="spg-card-badge"
                >
                  {{ quantidadeAdicionada(produto.id) }}
                </b-badge>
              </div>
              <div class="spg-card-corpo">
                <div class="spg-card-nome">{{ produto.descricao }}</div>
                <div class="spg-card-preco">
                  {{ formatMoney(produto.preco) }}
                </div>
                <div
                  v-if="requiresConfiguration(produto)"
                  class="spg-card-info"
                >
                  <i class="fas fa-sliders-h" aria-hidden="true"></i>
                  Possui opções
                </div>
              </div>
              <div class="spg-card-acoes">
                <button
                  v-if="!requiresConfiguration(produto)"
                  type="button"
                  class="btn btn-sm btn-primary spg-card-acao"
                  :aria-label="`Adicionar ${produto.descricao}`"
                  @click="adicionarRapido(produto)"
                >
                  <i class="fas fa-plus" aria-hidden="true"></i>
                  Adicionar
                </button>
                <button
                  v-else
                  type="button"
                  class="btn btn-sm btn-primary spg-card-acao"
                  :aria-label="`Configurar ${produto.descricao}`"
                  @click="configurarProduto(produto)"
                >
                  <i class="fas fa-sliders-h" aria-hidden="true"></i>
                  Configurar
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div
        v-if="carregando && produtosAchatados.length"
        class="spg-atualizando"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
        Atualizando resultados...
      </div>

      <div
        v-if="erro && produtosAchatados.length"
        class="spg-erro-inline"
        role="alert"
      >
        <span>{{ erro }}</span>
        <button type="button" class="btn btn-sm btn-link" @click="recarregar">
          Tentar novamente
        </button>
      </div>

      <div v-if="temMais || carregandoMais" class="spg-paginacao">
        <button
          type="button"
          class="btn btn-sm btn-light-primary"
          :disabled="carregandoMais"
          @click="carregarMais"
        >
          <i
            v-if="carregandoMais"
            class="fas fa-spinner fa-spin mr-1"
            aria-hidden="true"
          ></i>
          {{ carregandoMais ? "Carregando mais..." : labelCarregarMais }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  normalizeOperationalCatalog,
  buildQuickComboConfiguration,
} from "../services/restaurantCatalog";
import { isMissingEndpointError } from "../services/onlineOrder";

export default {
  name: "SeletorProdutosGarcom",
  props: {
    // Quantidades ja adicionadas na sessao, no formato { [produtoId]: numero }
    // Permite mostrar o badge de contagem no card.
    quantidades: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      categorias: [],
      grupos: [],
      carregando: false,
      carregandoMais: false,
      erro: "",
      busca: "",
      categoriaAtiva: null,
      carregado: false,
      pagina: 0,
      ultimaPagina: 1,
      totalProdutos: 0,
      porPagina: 24,
      requestSequence: 0,
      debounceBuscaTimer: null,
      consultaPendente: false,
      usandoCatalogoLegado: false,
      catalogoLegado: null,
    };
  },
  // Auto-carrega o cardapio assim que o componente eh montado. Isso torna o
  // carregamento robusto contra condicoes de corrida (o modal do BootstrapVue
  // pode renderizar o conteudo de forma assincrona, fazendo a chamada externa
  // via $ref falhar por timing).
  mounted() {
    this.carregarCardapio();
  },
  beforeDestroy() {
    this.cancelarDebounceBusca();
    this.requestSequence += 1;
  },
  watch: {
    busca() {
      this.agendarBusca();
    },
  },
  computed: {
    produtosAchatados() {
      return this.grupos.flatMap((grupo) => grupo.produtos || []);
    },
    resultadosBusca() {
      if (!this.usandoCatalogoLegado) {
        return this.produtosAchatados;
      }

      const termo = this.normalizar(this.busca);
      if (!termo) {
        return this.produtosAchatados;
      }
      return this.produtosAchatados.filter((produto) => {
        return (
          this.normalizar(produto.descricao).includes(termo) ||
          this.normalizar(produto.codigo).includes(termo) ||
          this.normalizar(produto.codigo_barras).includes(termo) ||
          String(produto.id || "") === termo
        );
      });
    },
    gruposVisiveis() {
      if (!this.usandoCatalogoLegado || this.categoriaAtiva === null) {
        return this.grupos;
      }
      return this.grupos.filter((grupo) => grupo.id === this.categoriaAtiva);
    },
    temMais() {
      return !this.usandoCatalogoLegado && this.pagina < this.ultimaPagina;
    },
    labelCarregarMais() {
      const restantes = Math.max(
        Number(this.totalProdutos || 0) - this.produtosAchatados.length,
        0
      );
      return restantes > 0
        ? `Carregar mais (${restantes} restantes)`
        : "Carregar mais";
    },
  },
  methods: {
    focusBusca() {
      this.$refs.buscaInput?.focus();
    },
    async carregarCardapio(force = false) {
      if (!force && this.carregado) {
        return;
      }

      this.cancelarDebounceBusca();
      const requestSequence = ++this.requestSequence;
      return this.carregarPagina(1, false, requestSequence);
    },
    async carregarPagina(page, append = false, requestSequence = null) {
      const sequence = requestSequence || ++this.requestSequence;
      const isFirstPage = Number(page) === 1;
      if (isFirstPage) {
        this.carregando = true;
      } else {
        this.carregandoMais = true;
      }
      this.erro = "";

      try {
        if (this.usandoCatalogoLegado && this.catalogoLegado) {
          this.aplicarCatalogoLegado(sequence);
          return;
        }

        let response = null;
        try {
          response = await this.carregarCatalogoEstruturado(page);
        } catch (error) {
          if (!isMissingEndpointError(error)) {
            throw error;
          }

          const legacyResponse = await this.$http.post(
            "/produtos/restaurante/cardapio-garcom"
          );
          if (sequence !== this.requestSequence) {
            return;
          }
          this.usandoCatalogoLegado = true;
          this.catalogoLegado = normalizeOperationalCatalog(
            legacyResponse.data
          );
          this.registrarCategorias(this.catalogoLegado);
          this.aplicarCatalogoLegado(sequence);
          return;
        }

        if (sequence !== this.requestSequence) {
          return;
        }

        const payload = response.data?.data ?? response.data ?? {};
        const pagination = payload.pagination || {};
        const novosGrupos = normalizeOperationalCatalog(response.data);
        const recebeuCategoriasCompletas = Array.isArray(payload.categorias);
        this.registrarCategorias(
          recebeuCategoriasCompletas ? payload.categorias : novosGrupos,
          recebeuCategoriasCompletas
        );
        this.grupos = append
          ? this.mesclarGrupos(this.grupos, novosGrupos)
          : novosGrupos;
        this.pagina = Number(pagination.current_page || page || 1);
        this.ultimaPagina = Math.max(
          Number(pagination.last_page || this.pagina),
          this.pagina
        );
        this.totalProdutos = Number(
          pagination.total || this.produtosAchatados.length
        );
        this.porPagina = Number(pagination.per_page || this.porPagina);
        this.carregado = true;
      } catch (error) {
        if (sequence === this.requestSequence) {
          this.erro = "Não foi possível carregar os produtos. Tente novamente.";
        }
      } finally {
        if (sequence === this.requestSequence) {
          this.carregando = false;
          this.carregandoMais = false;
          this.consultaPendente = false;
        }
      }
    },
    carregarCatalogoEstruturado(page = 1) {
      return this.$http.get("/restaurante/catalogo-operacional/produtos", {
        params: {
          q: this.busca.trim() || undefined,
          grupo_id:
            this.categoriaAtiva === null ? undefined : this.categoriaAtiva,
          page,
          per_page: this.porPagina,
        },
      });
    },
    recarregar() {
      this.carregarCardapio(true);
    },
    carregarMais() {
      if (
        !this.temMais ||
        this.carregando ||
        this.carregandoMais ||
        this.consultaPendente
      ) {
        return;
      }

      const requestSequence = ++this.requestSequence;
      this.carregarPagina(this.pagina + 1, true, requestSequence);
    },
    onCatalogScroll(event) {
      const container = event && event.target;
      if (!container) {
        return;
      }
      const distanceToEnd =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceToEnd <= 240) {
        this.carregarMais();
      }
    },
    agendarBusca() {
      this.cancelarDebounceBusca();
      const requestSequence = ++this.requestSequence;
      this.consultaPendente = true;
      this.carregando = true;
      this.carregandoMais = false;
      this.erro = "";
      this.grupos = [];
      this.debounceBuscaTimer = setTimeout(() => {
        this.debounceBuscaTimer = null;
        this.consultaPendente = false;
        this.carregarPagina(1, false, requestSequence);
      }, 350);
    },
    cancelarDebounceBusca() {
      if (this.debounceBuscaTimer) {
        clearTimeout(this.debounceBuscaTimer);
        this.debounceBuscaTimer = null;
      }
      this.consultaPendente = false;
    },
    limparBusca() {
      this.busca = "";
    },
    selecionarCategoria(id) {
      if (this.categoriaAtiva === id) {
        return;
      }

      this.cancelarDebounceBusca();
      this.categoriaAtiva = id;
      this.grupos = [];
      this.rolarChipAtivoParaVisivel(id);
      const requestSequence = ++this.requestSequence;
      this.carregarPagina(1, false, requestSequence);
    },
    registrarCategorias(grupos, substituir = false) {
      const categoriesById = new Map(
        (substituir ? [] : this.categorias).map((categoria) => [
          Number(categoria.id),
          categoria,
        ])
      );
      (grupos || []).forEach((grupo) => {
        const groupId = Number(grupo.id ?? grupo.grupo_id ?? 0);
        if (!Number.isFinite(groupId)) {
          return;
        }
        categoriesById.set(groupId, {
          id: groupId,
          nome: grupo.nome || grupo.grupo_nome || "Outros",
        });
      });
      this.categorias = Array.from(categoriesById.values()).sort(
        (left, right) => left.nome.localeCompare(right.nome, "pt-BR")
      );
    },
    mesclarGrupos(gruposAtuais, novosGrupos) {
      const groupsById = new Map();
      (gruposAtuais || []).forEach((grupo) => {
        groupsById.set(Number(grupo.id), {
          ...grupo,
          produtos: [...(grupo.produtos || [])],
        });
      });
      (novosGrupos || []).forEach((grupo) => {
        const groupId = Number(grupo.id);
        const current = groupsById.get(groupId) || {
          ...grupo,
          produtos: [],
        };
        const productsById = new Map(
          current.produtos.map((produto) => [Number(produto.id), produto])
        );
        (grupo.produtos || []).forEach((produto) => {
          productsById.set(Number(produto.id), produto);
        });
        groupsById.set(groupId, {
          ...current,
          ...grupo,
          produtos: Array.from(productsById.values()),
        });
      });
      return Array.from(groupsById.values());
    },
    aplicarCatalogoLegado(requestSequence) {
      if (requestSequence !== this.requestSequence) {
        return;
      }
      const termo = this.normalizar(this.busca);
      const grupos = (this.catalogoLegado || [])
        .filter(
          (grupo) =>
            this.categoriaAtiva === null ||
            Number(grupo.id) === Number(this.categoriaAtiva)
        )
        .map((grupo) => ({
          ...grupo,
          produtos: (grupo.produtos || []).filter((produto) => {
            if (!termo) {
              return true;
            }
            return (
              this.normalizar(produto.descricao).includes(termo) ||
              this.normalizar(produto.codigo).includes(termo) ||
              this.normalizar(produto.codigo_barras).includes(termo) ||
              String(produto.id || "") === termo
            );
          }),
        }))
        .filter((grupo) => grupo.produtos.length);

      this.grupos = grupos;
      this.pagina = 1;
      this.ultimaPagina = 1;
      this.totalProdutos = this.produtosAchatados.length;
      this.carregado = true;
    },
    rolarChipAtivoParaVisivel(id) {
      this.$nextTick(() => {
        const chips = this.$el.querySelectorAll(".spg-chip");
        const alvo =
          id === null
            ? chips[0]
            : Array.from(chips).find((chip) =>
                chip.classList.contains("spg-chip-ativo")
              );
        if (alvo && typeof alvo.scrollIntoView === "function") {
          alvo.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      });
    },
    adicionarRapido(produto) {
      this.$emit("quick-add", produto);
    },
    requiresConfiguration(produto) {
      return Boolean(
        produto?.configuravel && !buildQuickComboConfiguration(produto)
      );
    },
    configurarProduto(produto) {
      if (produto?.configuravel) {
        this.$emit("configure", produto);
      }
    },
    quantidadeAdicionada(id) {
      return Number(this.quantidades?.[id] || 0);
    },
    iniciais(nome) {
      const texto = String(nome || "").trim();
      if (!texto) {
        return "?";
      }
      return texto.substring(0, 2).toUpperCase();
    },
    imagemStyle(produto) {
      if (!produto.imagem) {
        return {};
      }
      return { backgroundImage: `url('${produto.imagem}')` };
    },
    normalizar(texto) {
      return String(texto || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    formatMoney(valor) {
      const numero = Number(valor || 0);
      return numero.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },
};
</script>

<style scoped>
.seletor-produtos-garcom {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* Toolbar fixo no topo */
.spg-toolbar {
  flex: 0 0 auto;
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  padding-bottom: 8px;
}

.spg-busca {
  margin-bottom: 8px;
}
.spg-busca-input {
  position: relative;
  display: flex;
  align-items: center;
}
.spg-busca-icone {
  position: absolute;
  left: 10px;
  color: #b5b5c3;
  font-size: 0.8rem;
  pointer-events: none;
}
.spg-busca-input input {
  padding-left: 30px;
  border-radius: 8px;
  background: #f3f6f9;
  border-color: transparent;
  min-height: 44px;
}
.spg-busca-input input:focus {
  background: #fff;
  border-color: #3699ff;
}
.spg-busca-limpar {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  color: #b5b5c3;
  cursor: pointer;
  height: 44px;
  padding: 4px;
  font-size: 0.8rem;
  width: 44px;
}

/* Chips de categoria (rolam horizontalmente) */
.spg-chips {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 2px 0 6px;
  scrollbar-width: thin;
}
.spg-chips::-webkit-scrollbar {
  height: 4px;
}
.spg-chips::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}
.spg-chip {
  flex: 0 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  color: #3f4254;
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  min-height: 44px;
}
.spg-chip-ativo {
  background: #3699ff;
  color: #fff;
  border-color: #3699ff;
}

/* Corpo rolavel: ocupa o espaco restante do flex */
.spg-corpo {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 180px;
}

.spg-estado {
  text-align: center;
  color: #b5b5c3;
  padding: 28px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.spg-estado-load i {
  margin-right: 6px;
}
.spg-estado-erro {
  color: #f64e60;
}
.spg-vazio-busca {
  grid-column: 1 / -1;
}
.spg-atualizando,
.spg-erro-inline,
.spg-paginacao {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 44px;
  padding: 10px 8px;
  text-align: center;
}
.spg-atualizando {
  color: #7e8299;
  gap: 6px;
}
.spg-erro-inline {
  color: #f64e60;
  flex-wrap: wrap;
}
.spg-paginacao .btn {
  min-height: 44px;
  min-width: 160px;
}

/* Grid de produtos: denso e responsivo */
.spg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: 8px;
}
.spg-card {
  display: flex;
  flex-direction: column;
  text-align: left;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 6px;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  width: 100%;
  min-height: 44px;
}
.spg-card:hover {
  border-color: #3699ff;
  box-shadow: 0 2px 8px rgba(54, 153, 255, 0.12);
}
.spg-card-adicionado {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.25);
}

.spg-card-topo {
  position: relative;
  width: 100%;
}
.spg-card-imagem {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  background-color: #f3f6f9;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}
.spg-card-iniciais {
  font-weight: 700;
  color: #b5b5c3;
  font-size: 1rem;
}
.spg-card-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.spg-card-corpo {
  padding: 6px 4px 2px;
}
.spg-card-nome {
  font-weight: 600;
  font-size: 0.78rem;
  color: #181c32;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 1.9em;
}
.spg-card-preco {
  font-size: 0.78rem;
  color: #3699ff;
  font-weight: 700;
  margin-top: 2px;
}
.spg-card-info {
  color: #8950fc;
  font-size: 0.68rem;
  font-weight: 600;
  margin-top: 3px;
}
.spg-card-acoes {
  display: grid;
  gap: 4px;
  margin-top: auto;
  padding: 4px;
}
.spg-card-acao {
  align-items: center;
  display: inline-flex;
  font-size: 0.72rem;
  gap: 5px;
  justify-content: center;
  min-height: 44px;
  padding: 5px 7px;
  white-space: nowrap;
}
.spg-card-acao:focus-visible,
.spg-chip:focus-visible,
.spg-busca-limpar:focus-visible {
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.28);
  outline: none;
}

.spg-categoria {
  margin-bottom: 14px;
}
.spg-categoria-titulo {
  font-weight: 700;
  font-size: 0.85rem;
  color: #181c32;
  margin: 4px 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 5;
}

/* Mobile: 2 colunas garantidas, cards um pouco maiores */
@media (max-width: 575.98px) {
  .spg-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  .spg-card-nome {
    font-size: 0.8rem;
  }
}
</style>
